import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Panel } from '../../components/Panel';
import { deviceLabel, isIOS, isStandalone } from '../../lib/device';
import { useAction } from '../../lib/useAction';
import { alertStatus, pushSupported, sendTestAlert, turnOffAlerts, turnOnAlerts } from './webPush';

const STATUS_KEY = ['alert-status'];
const needsHomeScreen = isIOS && !isStandalone;

function HomeScreenSteps() {
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
      <li>
        Tap the <b>Share</b> button (square with arrow) in Safari.
      </li>
      <li>
        Choose <b>Add to Home Screen</b>, then <b>Add</b>.
      </li>
      <li>
        Open <b>SERB</b> from your Home Screen, sign in with Face ID, and come back here.
      </li>
    </Box>
  );
}

export function AlertsPanel() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: STATUS_KEY, queryFn: alertStatus, enabled: pushSupported && !needsHomeScreen });
  const refresh = () => qc.invalidateQueries({ queryKey: STATUS_KEY });
  const on = useAction(turnOnAlerts, {
    success: (ok) => (ok ? 'Done. Now tap “Send a test alert”.' : undefined),
    onDone: () => void refresh(),
  });
  const off = useAction(turnOffAlerts, { success: 'Alerts turned off for this device.', onDone: () => void refresh() });
  const test = useAction(sendTestAlert, {
    success: (r) =>
      r.sent
        ? `Sent to ${r.sent} device(s). It should arrive in a few seconds${isIOS ? ' (lock the phone to see it on the lock screen)' : ''}.`
        : 'No device received it.' + (r.failed ? ' Sending failed, tell Claude.' : ''),
  });

  let state: string;
  const s = status.data;
  if (needsHomeScreen) state = 'On iPhone, alerts only work from the Home Screen app. Add SERB to your Home Screen:';
  else if (!pushSupported)
    state = 'This browser cannot receive alerts. On Mac use Safari; on iPhone use the Home Screen app.';
  else if (status.isError) state = 'Could not check alerts.';
  else if (!s) state = 'Checking…';
  else if (s.permission === 'denied')
    state = isIOS
      ? 'Alerts are blocked. Turn them on in iPhone Settings › Notifications › SERB.'
      : 'Alerts are blocked. Allow them in Safari › Settings › Websites › Notifications.';
  else state = `Alerts are ${s.on ? 'ON' : 'off'} for this ${deviceLabel}.`;

  return (
    <Panel title="Alerts on this device" subtitle={state}>
      {needsHomeScreen && <HomeScreenSteps />}
      {s && s.permission !== 'denied' && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {s.on ? (
            <>
              <Button variant="outlined" disabled={test.busy} onClick={() => test.run(undefined)}>
                Send a test alert
              </Button>
              <Button color="inherit" disabled={off.busy} onClick={() => off.run(undefined)}>
                Turn off on this device
              </Button>
            </>
          ) : (
            <Button variant="contained" disabled={on.busy} onClick={() => on.run(undefined)}>
              Turn on alerts
            </Button>
          )}
        </Stack>
      )}
      {s && (
        <Typography variant="body2" color="text.secondary">
          {s.devices.length
            ? 'Alerts go to: ' + s.devices.map((d) => d.label).join(', ')
            : 'No device has alerts on yet.'}
        </Typography>
      )}
    </Panel>
  );
}
